import { memo, useEffect } from 'react';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import shadows, { Shadows } from '@mui/material/styles/shadows';
import { CoreSliceActions } from 'base/core/slice';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectLoading, selectTheme, selectEnterprise,
} from 'base/core/selectors';
import { getCurrentTenant } from 'utils/common-utils';
import { Loader } from 'components';

interface Props {
  children: any;
}

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      borderRadius: string;
      boxShadow: string;
      header: {
        height: {
          xs: number;
          md: number;
        };
      };
    };
  }
  interface ThemeOptions {
    custom?: {
      borderRadius?: string;
      boxShadow?: string;
      header?: {
        height: {
          xs: number;
          md: number;
        };
      };
    };
  }
}

const ThemeWrapper = memo(({ children }: Props) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);
  const themeData = useSelector(selectTheme);
  const { entLogo, name } = useSelector(selectEnterprise);

  useEffect(() => {
    async function fetchData() {
      const tenant = getCurrentTenant();
      await new Promise((resolve, reject) => {
        dispatch(CoreSliceActions.fetchTenant({
          slug: tenant,
          resolve,
          reject,
        }));
      });
    }
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    let link: any = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = entLogo;

    document.title = `${name} - Expert Hub`;
  }, [entLogo, name]);

  const bodyFontFamily = [
    themeData.isFontDefault ? 'Urbanist' : `"${themeData.bodyFont.name}"`,
    'Roboto',
    'sans-serif',
  ].join(',');

  const headingFontFamily = [
    themeData.isFontDefault ? 'Urbanist' : `"${themeData.headerFont.name}"`,
    'Roboto',
    'sans-serif',
  ].join(',');

  let theme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        light: '#E7E7F1',
        main: themeData.colors.primaryColor,
        dark: '#CDCDDF',
      },
      secondary: {
        light: '#DBFFF1',
        main: themeData.colors.secondaryColor,
        dark: '#2F9A72',
      },
      grey: {
        A100: '#F3F3F8',
        A400: '#CDCDCD',
        A700: '#999999',
        900: '#333333',
      },
      text: {
        primary: '#000000',
        secondary: '#999999',
        disabled: '#CDCDCD',
      },
      action: {
        active: '#FFFFFF',
        hover: '#FFFFFF',
        selected: '#FFFFFF',
        disabled: '#FFFFFF',
        disabledBackground: '#CDCDCD',
      },
      background: {
        paper: '#FFFFFF',
        default: '#FAFAFC',
      },
      divider: '#E7E7F1',
    },
    typography: {
      htmlFontSize: 16,
      fontFamily: bodyFontFamily,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontFamily: headingFontFamily,
        fontSize: '3.375rem',
        fontWeight: 'bold',
        lineHeight: '65px',
        letterSpacing: 0,
      },
      h2: {
        fontFamily: headingFontFamily,
        fontSize: '2.5rem',
        fontWeight: 'bold',
        lineHeight: '48px',
        letterSpacing: 0,
      },
      h3: {
        fontFamily: headingFontFamily,
        fontSize: '1.875rem',
        fontWeight: 'bold',
        lineHeight: '37px',
        letterSpacing: 0,
      },
      h4: {
        fontFamily: headingFontFamily,
        fontSize: '1.5rem',
        fontWeight: 'bold',
        lineHeight: '29px',
        letterSpacing: 0,
      },
      h5: {
        fontFamily: headingFontFamily,
        fontSize: '1.25rem',
        fontWeight: 'bold',
        lineHeight: '24px',
        letterSpacing: 0,
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 'bold',
        lineHeight: '24px',
        letterSpacing: 0,
      },
      body1: {
        fontSize: '1.125rem',
        fontWeight: 500,
        letterSpacing: 0,
        color: '#181F26',
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 600,
        letterSpacing: 0,
        color: '#333333',
        lignHeight: '17px',
      },
      subtitle1: {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: '17px',
        letterSpacing: '2.59px',
        color: '#181F26',
        textTransform: 'uppercase',
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: '17px',
        letterSpacing: '2.02px',
        textTransform: 'uppercase',
      },
    },
    custom: {
      borderRadius: '6px',
      boxShadow: '0px 8px 60px #00000005',
      header: {
        height: {
          xs: 60,
          md: 80,
        },
      },
    },
    shadows: shadows.map(() => 'none') as Shadows,
    zIndex: {
      appBar: 997,
      drawer: 998,
    },
  });

  theme = createTheme(theme, {
    breakpoints: {
      values: {
        xs: 0,
        sm: 576,
        md: 768,
        lg: 992,
        xl: 1200,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: themeData.isFontDefault ? '' : `
          * {
            scrollbar-width: auto;
            scrollbar-color: ${theme.palette.primary.dark} ${theme.palette.grey.A100};
          }
          /* Chrome, Edge, and Safari */
          *::-webkit-scrollbar {
            width: 8px;
          }
          *::-webkit-scrollbar-track {
            background: ${theme.palette.grey.A100};
          }
          *::-webkit-scrollbar-thumb {
            background-color: ${theme.palette.primary.dark};
            border-radius: 6px;
            border: 0px solid ${theme.palette.primary.dark};
          }
          ::-webkit-scrollbar:horizontal {
            height: 8px;
          }
          *::-webkit-scrollbar-track:horizontal {
            background: ${theme.palette.grey.A100};
          }
          ::-webkit-scrollbar-thumb:horizontal {
            background-color: ${theme.palette.primary.dark};
            border-radius: 6px;
            border: 0px solid ${theme.palette.primary.dark};
          }
          @font-face {
            font-family: '${themeData.bodyFont.name}';
            font-style: normal;
            font-display: swap;
            src: url(${themeData.bodyFont.url}) format("woff");
          }
          @font-face {
            font-family: '${themeData.headerFont.name}';
            font-style: normal;
            font-display: swap;
            src: url(${themeData.headerFont.url}) format("woff");
          }
        `,
      },
      MuiContainer: {
        styleOverrides: {
          maxWidthLg: {
            [theme.breakpoints.up('xs')]: {
              maxWidth: '540px',
              paddingLeft: '12px',
              paddingRight: '12px',
            },
            [theme.breakpoints.up('sm')]: {
              maxWidth: '720px',
              paddingLeft: '12px',
              paddingRight: '12px',
            },
            [theme.breakpoints.up('md')]: {
              maxWidth: '960px',
              paddingLeft: '12px',
              paddingRight: '12px',
            },
            [theme.breakpoints.up('lg')]: {
              maxWidth: '1248px',
              paddingLeft: '12px',
              paddingRight: '12px',
            },
          },
        },
      },
    },
  });

  // set css theme variables
  const root = document.querySelector(':root') as HTMLElement;
  root.style.setProperty('--primary-main', themeData.colors.primaryColor);

  root.style.setProperty('--form-builder-primary-color', theme.palette.primary.main);
  root.style.setProperty(
    '--form-builder-primary-color-light',
    theme.palette.primary.light,
  );
  root.style.setProperty(
    '--form-builder-secondary-color',
    theme.palette.secondary.main,
  );
  root.style.setProperty(
    '--form-builder-secondary-color-light',
    theme.palette.secondary.light,
  );

  return (
    <ThemeProvider theme={theme}>
      <Loader loading={loading}>
        {children}
      </Loader>
    </ThemeProvider>
  );
});
export default ThemeWrapper;
